import jsPDF from "jspdf";
import { convertFromRaw } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { htmlToPlainText } from "../lib/contentToHtml";

const PDF_THEME = {
  accent: [242, 101, 34],
  accentDark: [217, 85, 24],
  accentSoft: [255, 245, 239],
  textPrimary: [51, 51, 51],
  textMuted: [119, 119, 119],
  border: [238, 238, 238],
  pageBg: [246, 246, 245],
  white: [255, 255, 255],
  fontSerif: "times",
  fontSans: "helvetica",
};

function setRgb(doc, [r, g, b]) {
  doc.setTextColor(r, g, b);
}

function setDrawRgb(doc, [r, g, b]) {
  doc.setDrawColor(r, g, b);
}

function setFillRgb(doc, [r, g, b]) {
  doc.setFillColor(r, g, b);
}

/**
 * Converts poem content to plain text with preserved line breaks, handling Draft.js format and HTML
 */
function poemContentToText(content) {
  try {
    let raw = content;
    if (typeof raw === "string") {
      try {
        raw = JSON.parse(raw);
        if (typeof raw === "string") {
          raw = JSON.parse(raw);
        }
      } catch {}
    }

    if (raw && raw.blocks && typeof raw.entityMap !== "undefined") {
      const contentState = convertFromRaw(raw);
      const html = stateToHTML(contentState);
      return htmlToPlainText(html);
    }

    if (typeof content === "string" && content.includes("<")) {
      return htmlToPlainText(content);
    }

    return typeof content === "string" ? content : String(content || "");
  } catch (error) {
    return typeof content === "string" ? content : String(content || "");
  }
}

/**
 * Splits text into lines preserving original line breaks
 */
function splitTextWithLineBreaks(text, maxWidth, doc) {
  const lines = [];
  const paragraphs = text.split(/\n\n+/);

  paragraphs.forEach((paragraph, pIndex) => {
    if (pIndex > 0) {
      lines.push("");
    }
    const paragraphLines = paragraph.split(/\n/);
    paragraphLines.forEach((line) => {
      if (line.trim() === "") {
        lines.push("");
      } else {
        const wrappedLines = doc.splitTextToSize(line.trim(), maxWidth);
        lines.push(...wrappedLines);
      }
    });
  });

  return lines;
}

/**
 * PeerPoetry wordmark: "Peer" in dark + "Poetry" in accent orange
 */
function drawBrandLogo(doc, x, y, size = 16, align = "center") {
  const pageWidth = doc.internal.pageSize.getWidth();
  let xPos = x;

  if (align === "center") {
    xPos = pageWidth / 2;
  } else if (align === "right") {
    xPos = pageWidth - x;
  }

  doc.setFontSize(size);
  doc.setFont(PDF_THEME.fontSerif, "bold");

  const peerText = "Peer";
  const poetryText = "Poetry";
  const peerWidth = doc.getTextWidth(peerText);
  const poetryWidth = doc.getTextWidth(poetryText);
  const totalWidth = peerWidth + poetryWidth;

  let peerX = xPos;
  if (align === "center") {
    peerX = xPos - totalWidth / 2;
  } else if (align === "right") {
    peerX = xPos - totalWidth;
  }

  setRgb(doc, PDF_THEME.textPrimary);
  doc.text(peerText, peerX, y);

  setRgb(doc, PDF_THEME.accent);
  doc.text(poetryText, peerX + peerWidth, y);

  const lineY = y + 3;
  setDrawRgb(doc, PDF_THEME.accent);
  doc.setLineWidth(size >= 18 ? 0.8 : 0.5);
  doc.line(peerX, lineY, peerX + totalWidth, lineY);

  setRgb(doc, PDF_THEME.textPrimary);
}

function drawAccentRule(doc, margin, pageWidth, y, thickness = 0.5) {
  setDrawRgb(doc, PDF_THEME.accent);
  doc.setLineWidth(thickness);
  doc.line(margin, y, pageWidth - margin, y);
}

function drawMutedRule(doc, margin, pageWidth, y) {
  setDrawRgb(doc, PDF_THEME.border);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageWidth - margin, y);
}

function drawPageHeaderBand(doc, pageWidth) {
  setFillRgb(doc, PDF_THEME.accentSoft);
  doc.rect(0, 0, pageWidth, 14, "F");
  setFillRgb(doc, PDF_THEME.accent);
  doc.rect(0, 14, pageWidth, 1.5, "F");
}

function formatPoemDate(value) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function getAuthorLabel(author) {
  if (!author) return "Unknown Author";
  return author.username || author.name || "Unknown Author";
}

function drawCopyrightLine(doc, poem, margin, pageWidth, y) {
  if (!poem?.copyrightEnabled) return y;

  const holder = (poem.copyrightHolder || poem.author?.username || "").trim();
  const year = poem.copyrightYear || new Date(poem.createdAt || Date.now()).getFullYear();
  if (!holder) return y;

  doc.setFontSize(9);
  doc.setFont(PDF_THEME.fontSans, "italic");
  setRgb(doc, PDF_THEME.textMuted);
  doc.text(`© ${year} ${holder}`, pageWidth / 2, y, { align: "center" });
  return y + 8;
}

function drawPoemBody(doc, poem, margin, maxWidth, pageHeight, getY, setY, checkPageBreak) {
  const poemText = poemContentToText(poem.content);
  doc.setFontSize(13);
  doc.setFont(PDF_THEME.fontSerif, "normal");
  setRgb(doc, PDF_THEME.textPrimary);

  const contentLines = splitTextWithLineBreaks(poemText, maxWidth, doc);

  contentLines.forEach((line) => {
    const lineHeight = line.trim() === "" ? 5 : 7;
    checkPageBreak(lineHeight);
    doc.text(line || " ", margin, getY());
    setY(getY() + lineHeight);
  });
}

function drawPageFooter(doc, pageIndex, totalPages, margin, pageWidth, pageHeight) {
  drawMutedRule(doc, margin, pageWidth, pageHeight - 22);
  drawBrandLogo(doc, pageWidth / 2, pageHeight - 16, 9, "center");

  doc.setFontSize(7);
  doc.setFont(PDF_THEME.fontSans, "normal");
  setRgb(doc, PDF_THEME.textMuted);
  doc.text(`Page ${pageIndex} of ${totalPages}`, pageWidth / 2, pageHeight - 5, {
    align: "center",
  });
}

function createPdfContext() {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 28;
  const maxWidth = pageWidth - 2 * margin;
  let yPosition = margin + 8;

  const getY = () => yPosition;
  const setY = (value) => {
    yPosition = value;
  };

  const checkPageBreak = (requiredHeight) => {
    if (yPosition + requiredHeight > pageHeight - margin - 18) {
      doc.addPage();
      drawPageHeaderBand(doc, pageWidth);
      yPosition = margin + 8;
      return true;
    }
    return false;
  };

  return { doc, pageWidth, pageHeight, margin, maxWidth, getY, setY, checkPageBreak };
}

/**
 * Downloads a poem as PDF
 */
export function downloadPoemAsPDF(poem, author) {
  try {
    const { doc, pageWidth, pageHeight, margin, maxWidth, getY, setY, checkPageBreak } =
      createPdfContext();

    drawPageHeaderBand(doc, pageWidth);
    drawBrandLogo(doc, pageWidth / 2, getY(), 18, "center");
    setY(getY() + 18);
    drawAccentRule(doc, margin, pageWidth, getY());
    setY(getY() + 14);

    doc.setFontSize(24);
    doc.setFont(PDF_THEME.fontSerif, "bold");
    setRgb(doc, PDF_THEME.textPrimary);
    const titleLines = doc.splitTextToSize(poem.title || "Untitled Poem", maxWidth);
    titleLines.forEach((line) => {
      checkPageBreak(12);
      doc.text(line, pageWidth / 2, getY(), { align: "center" });
      setY(getY() + 10);
    });

    setY(getY() + 4);

    doc.setFontSize(12);
    doc.setFont(PDF_THEME.fontSans, "italic");
    setRgb(doc, PDF_THEME.textMuted);
    doc.text(`By ${getAuthorLabel(author)}`, pageWidth / 2, getY(), { align: "center" });
    setY(getY() + 8);

    if (poem.createdAt) {
      doc.setFontSize(10);
      doc.setFont(PDF_THEME.fontSans, "normal");
      doc.text(formatPoemDate(poem.createdAt), pageWidth / 2, getY(), { align: "center" });
      setY(getY() + 10);
    }

    setY(drawCopyrightLine(doc, poem, margin, pageWidth, getY()));
    drawMutedRule(doc, margin, pageWidth, getY());
    setY(getY() + 14);

    drawPoemBody(doc, poem, margin, maxWidth, pageHeight, getY, setY, checkPageBreak);

    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      drawPageFooter(doc, i, totalPages, margin, pageWidth, pageHeight);
    }

    const fileName = `${(poem.title || "poem").replace(/[^a-z0-9]/gi, "_")}_${Date.now()}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw new Error("Failed to generate PDF. Please try again.");
  }
}

/**
 * Downloads multiple poems as a single PDF
 */
export function downloadMultiplePoemsAsPDF(poems, author) {
  if (!poems || poems.length === 0) {
    throw new Error("No poems selected");
  }

  try {
    const { doc, pageWidth, pageHeight, margin, maxWidth, getY, setY, checkPageBreak } =
      createPdfContext();

    setFillRgb(doc, PDF_THEME.accentSoft);
    doc.rect(0, 0, pageWidth, pageHeight, "F");
    drawPageHeaderBand(doc, pageWidth);

    drawBrandLogo(doc, pageWidth / 2, 58, 24, "center");

    doc.setFontSize(30);
    doc.setFont(PDF_THEME.fontSerif, "bold");
    setRgb(doc, PDF_THEME.textPrimary);
    doc.text("My Poems", pageWidth / 2, pageHeight / 2 - 18, { align: "center" });

    doc.setFontSize(18);
    doc.setFont(PDF_THEME.fontSerif, "normal");
    setRgb(doc, PDF_THEME.accent);
    doc.text("Collection", pageWidth / 2, pageHeight / 2 + 2, { align: "center" });

    doc.setFontSize(14);
    doc.setFont(PDF_THEME.fontSans, "italic");
    setRgb(doc, PDF_THEME.textMuted);
    doc.text(`By ${getAuthorLabel(author)}`, pageWidth / 2, pageHeight / 2 + 24, {
      align: "center",
    });

    doc.setFontSize(10);
    doc.setFont(PDF_THEME.fontSans, "normal");
    doc.text(
      `Generated on ${formatPoemDate(new Date())}`,
      pageWidth / 2,
      pageHeight / 2 + 40,
      { align: "center" }
    );
    doc.text(
      `${poems.length} ${poems.length === 1 ? "Poem" : "Poems"}`,
      pageWidth / 2,
      pageHeight / 2 + 52,
      { align: "center" }
    );

    doc.addPage();
    drawPageHeaderBand(doc, pageWidth);
    setY(margin + 8);
    drawBrandLogo(doc, pageWidth / 2, getY(), 16, "center");
    setY(getY() + 22);

    doc.setFontSize(20);
    doc.setFont(PDF_THEME.fontSerif, "bold");
    setRgb(doc, PDF_THEME.textPrimary);
    doc.text("Table of Contents", margin, getY());
    setY(getY() + 12);
    drawAccentRule(doc, margin, pageWidth, getY(), 0.4);
    setY(getY() + 10);

    doc.setFontSize(11);
    doc.setFont(PDF_THEME.fontSans, "normal");
    setRgb(doc, PDF_THEME.textPrimary);

    poems.forEach((poem, index) => {
      checkPageBreak(8);
      const poemTitle = poem.title || "Untitled Poem";
      const titleLines = doc.splitTextToSize(`${index + 1}. ${poemTitle}`, maxWidth - 20);
      titleLines.forEach((line) => {
        doc.text(line, margin + 4, getY());
        setY(getY() + 6);
      });
      setY(getY() + 2);
    });

    doc.addPage();
    drawPageHeaderBand(doc, pageWidth);
    setY(margin + 8);

    poems.forEach((poem, index) => {
      if (index > 0) {
        doc.addPage();
        drawPageHeaderBand(doc, pageWidth);
        setY(margin + 8);
      }

      drawBrandLogo(doc, pageWidth / 2, getY(), 14, "center");
      setY(getY() + 18);
      drawAccentRule(doc, margin, pageWidth, getY());
      setY(getY() + 12);

      doc.setFontSize(10);
      doc.setFont(PDF_THEME.fontSans, "normal");
      setRgb(doc, PDF_THEME.textMuted);
      doc.text(`Poem ${index + 1} of ${poems.length}`, margin, getY());
      setY(getY() + 10);

      doc.setFontSize(22);
      doc.setFont(PDF_THEME.fontSerif, "bold");
      setRgb(doc, PDF_THEME.textPrimary);
      const titleLines = doc.splitTextToSize(poem.title || "Untitled Poem", maxWidth);
      titleLines.forEach((line) => {
        checkPageBreak(12);
        doc.text(line, margin, getY());
        setY(getY() + 10);
      });

      setY(getY() + 2);

      if (poem.createdAt) {
        doc.setFontSize(10);
        doc.setFont(PDF_THEME.fontSans, "normal");
        setRgb(doc, PDF_THEME.textMuted);
        doc.text(formatPoemDate(poem.createdAt), margin, getY());
        setY(getY() + 8);
      }

      setY(drawCopyrightLine(doc, poem, margin, pageWidth, getY()));
      drawMutedRule(doc, margin, pageWidth, getY());
      setY(getY() + 12);

      drawPoemBody(doc, poem, margin, maxWidth, pageHeight, getY, setY, checkPageBreak);
    });

    const totalPages = doc.internal.pages.length - 1;
    for (let i = 2; i <= totalPages; i++) {
      doc.setPage(i);
      drawPageFooter(doc, i - 1, totalPages - 1, margin, pageWidth, pageHeight);
    }

    const fileName = `My_Poems_Collection_${Date.now()}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error("Error generating PDF:", error);
    throw new Error("Failed to generate PDF. Please try again.");
  }
}
